//  filepath: src/components/dashboard/YoyChartCard.jsx
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { fmtUSDWhole, compactUSDWhole } from "../../utils/number";

export default function YoyChartCard({ tab, yoyBars }) {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 640);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const series = tab === "Revenue" ? yoyBars.revenue : yoyBars.reservations;

  const yTickFormatter =
    tab === "Revenue"
      ? (v) => (isMobile ? compactUSDWhole(v) : fmtUSDWhole(v))
      : (v) =>
          new Intl.NumberFormat("en-US", {
            notation: isMobile ? "compact" : "standard",
            maximumFractionDigits: 1,
          }).format(Number(v ?? 0));

  const yearNow = new Date().getUTCFullYear();
  const yearPrev = yearNow - 1;

  const data = (Array.isArray(series) ? series : []).map((m) => ({
    month: m.label,
    curr: m.curr,
    prev: m.prev,
  }));

  const CustomYoyTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const map = Object.fromEntries(payload.map((p) => [p.dataKey, p.value]));
    const prev = Number(map.prev ?? 0);
    const curr = Number(map.curr ?? 0);
    const hasBoth = Number.isFinite(prev) && Number.isFinite(curr);
    const deltaAbs = hasBoth ? curr - prev : null;
    const deltaPct =
      hasBoth && prev > 0 ? Math.round(((curr - prev) / prev) * 100) : null;
    const isUp = deltaAbs != null && deltaAbs > 0;
    const isDown = deltaAbs != null && deltaAbs < 0;

    const fmtVal =
      tab === "Revenue"
        ? (v) => (v == null ? "—" : fmtUSDWhole(v))
        : (v) =>
            v == null ? "—" : new Intl.NumberFormat("en-US").format(Number(v));

    return (
      <div className="pointer-events-none min-w-[240px] max-w-[320px] rounded-2xl border border-gray-100 bg-white/90 backdrop-blur-md shadow-2xl ring-1 ring-black/5">
        <div className="flex items-center justify-between px-4 py-3 rounded-t-2xl bg-gradient-to-r from-indigo-50 to-blue-50 border-b">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-blue-500/80" />
            <div className="text-sm font-semibold text-gray-900">{label}</div>
          </div>
          {deltaAbs != null && (
            <div
              className={[
                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium",
                isUp
                  ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                  : isDown
                  ? "bg-red-50 text-red-700 ring-1 ring-red-200"
                  : "bg-gray-50 text-gray-700 ring-1 ring-gray-200",
              ].join(" ")}
            >
              <span
                className={[
                  "inline-block h-1.5 w-1.5 rounded-full",
                  isUp
                    ? "bg-emerald-500"
                    : isDown
                    ? "bg-red-500"
                    : "bg-gray-400",
                ].join(" ")}
              />
              {isUp ? "Up" : isDown ? "Down" : "No change"}
              {deltaPct != null && (
                <span className="ml-1 font-semibold">
                  {deltaPct > 0 ? `+${deltaPct}%` : `${deltaPct}%`}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="px-4 py-3">
          <div className="grid grid-cols-1 gap-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span className="inline-block h-2.5 w-2.5 rounded-sm bg-sky-500" />
                <span className="font-medium">{yearNow}</span>
                <span className="hidden sm:inline text-gray-400">•</span>
                <span className="hidden sm:inline text-gray-500">
                  {tab === "Revenue" ? "Revenue" : "Reservations"}
                </span>
              </div>
              <div className="text-sm sm:text-base font-semibold text-gray-900">
                {fmtVal(map.curr)}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span className="inline-block h-2.5 w-2.5 rounded-sm bg-indigo-900" />
                <span className="font-medium">{yearPrev}</span>
                <span className="hidden sm:inline text-gray-400">•</span>
                <span className="hidden sm:inline text-gray-500">
                  {tab === "Revenue" ? "Revenue" : "Reservations"}
                </span>
              </div>
              <div className="text-sm sm:text-base font-medium text-gray-800">
                {fmtVal(map.prev)}
              </div>
            </div>
          </div>
          <div className="my-3 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Difference (abs)</span>
            <span
              className={[
                "font-semibold",
                isUp
                  ? "text-emerald-600"
                  : isDown
                  ? "text-red-600"
                  : "text-gray-600",
              ].join(" ")}
            >
              {tab === "Revenue"
                ? fmtVal(Math.abs(deltaAbs ?? 0))
                : (deltaAbs ?? 0).toLocaleString("en-US")}
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between text-xs">
            <span className="text-gray-500">Difference (%)</span>
            <span
              className={[
                "font-semibold",
                isUp
                  ? "text-emerald-600"
                  : isDown
                  ? "text-red-600"
                  : "text-gray-600",
              ].join(" ")}
            >
              {deltaPct == null
                ? "—"
                : deltaPct > 0
                ? `+${deltaPct}%`
                : `${deltaPct}%`}
            </span>
          </div>
        </div>
        <div className="h-1 rounded-b-2xl bg-gradient-to-r from-blue-500 via-sky-500 to-blue-600" />
      </div>
    );
  };

  return (
    <div className="relative w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-5">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
        {tab} – Year over Year by Month
      </h2>
      <div className="h-[360px] sm:h-[460px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={
              isMobile
                ? { top: 6, right: 12, left: 50, bottom: 22 }
                : { top: 6, right: 28, left: 72, bottom: 30 }
            }
          >
            <CartesianGrid
              stroke="#e5e7eb"
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              dataKey="month"
              tick={{ fontSize: isMobile ? 10 : 12, fill: "#6b7280" }}
              tickMargin={isMobile ? 6 : 10}
            />
            <YAxis
              tickFormatter={yTickFormatter}
              width={isMobile ? 60 : 96}
              tick={{ fontSize: isMobile ? 10 : 12, fill: "#6b7280" }}
              tickMargin={isMobile ? 4 : 8}
              label={{
                value: tab === "Revenue" ? "Total Revenue ($)" : "Reservations",
                angle: -90,
                position: "insideLeft",
                offset: isMobile ? 8 : 12,
                style: { fontSize: isMobile ? 10 : 12, fill: "#374151" },
              }}
            />
            <Tooltip
              content={<CustomYoyTooltip />}
              cursor={{ fill: "transparent" }}
            />
            <Legend />
            <Bar
              dataKey="curr"
              name={String(yearNow)}
              fill="#0ea5e9"
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="prev"
              name={String(yearPrev)}
              fill="#1e40af"
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
